import React, { useCallback, useMemo, useState } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, CalendarClock, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { cn } from 'shadcn/lib/utils';
import { PaginationControls, ResultsCounter } from 'shared';

import { PurchaseOrderActionsCell } from './PurchaseOrderActionsCell';
import { PurchaseOrderSortConfig } from './PurchaseOrderFilterConfig';
import { PurchaseOrderStatusCell } from './PurchaseOrderStatusCell';
import { PurchaseOrderLegendButton } from './PurchaseOrderTableLegends';
import { useBulkPurchaseOrderTableMutations } from './hooks/useBulkPurchaseOrderTableMutations';
import {
    formatPurchaseOrderDate,
    formatPurchaseOrderDateTime,
    isMidnightPurchaseOrderDateTime,
} from './utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatBuyerNames,
    isCrossDockPurchaseOrder,
    isLatePurchaseOrder,
    isNotSentToVendorPurchaseOrder,
    isPriorityPurchaseOrder,
} from './utils/purchaseOrderDisplayUtils';

const renderDeliveryCell = ({ getValue, row }) => {
    const rawValue = getValue();
    const formatted = formatPurchaseOrderDateTime(rawValue);
    const hasAppointment = !isMidnightPurchaseOrderDateTime(rawValue);
    const isLate = isLatePurchaseOrder(row?.original, rawValue);

    let iconColor = 'text-amber-500';
    let srLabel = 'No Appointment Scheduled';

    if (hasAppointment) {
        iconColor = 'text-emerald-600';
        srLabel = 'Appointment Scheduled';
    }

    if (isLate) {
        iconColor = 'text-red-600';
        srLabel = 'Late Delivery, No Appointment Scheduled';
    }

    return (
        <span className="inline-flex items-center gap-1">
            {formatted}
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex">
                        <CalendarClock className={`h-4 w-4 ${iconColor}`} aria-hidden="true" />
                    </span>
                </TooltipTrigger>
                <TooltipContent>{srLabel}</TooltipContent>
            </Tooltip>
            <span className="sr-only">{srLabel}</span>
        </span>
    );
};

const renderStatusCell = ({ getValue, row }) => (
    <PurchaseOrderStatusCell
        status={getValue()}
        fallbackText={FALLBACK_TEXT}
        isCrossDock={isCrossDockPurchaseOrder(row.original)}
        isPriority={isPriorityPurchaseOrder(row.original)}
        isNotSentToVendor={isNotSentToVendorPurchaseOrder(row.original)}
    />
);

const renderActionsCell = ({ row, table }) => {
    const {
        onCancelPurchaseOrder,
        onDownloadCsv,
        onDownloadPdf,
        onDownloadShipList,
        onDuplicatePurchaseOrder,
        onViewPurchaseOrder,
    } = table.options.meta ?? {};
    const isSendToVendorDisabled = table.options.meta?.isSendToVendorDisabled;
    const onSendToVendor = table.options.meta?.onSendToVendor;
    const lastSentAt = formatPurchaseOrderDateTime(row.original?.sentToVendor, '') || undefined;
    return (
        <PurchaseOrderActionsCell
            onViewClick={() => onViewPurchaseOrder?.(row.original)}
            onCancel={() => onCancelPurchaseOrder?.(row.original)}
            onDownloadCsv={() => onDownloadCsv?.(row.original)}
            onDownloadPdf={() => onDownloadPdf?.(row.original)}
            onDownloadShipList={() => onDownloadShipList?.(row.original)}
            onDuplicate={() => onDuplicatePurchaseOrder?.(row.original)}
            onSendToVendor={() => onSendToVendor?.(row.original)}
            isSendToVendorDisabled={isSendToVendorDisabled?.(row.original)}
            lastSentAt={lastSentAt}
        />
    );
};

/**
 * Returns a stable row identifier (purchase order id or TanStack row id).
 */
function getRowId(row) {
    return row.original?.id ?? row.id;
}

/**
 * Memoized row component to prevent unnecessary re-renders.
 * selectedRows drives row visual state (data-state) for selection styling.
 */
const MemoizedPurchaseOrderRow = React.memo(({ row, selectedRows }) => {
    const rowId = getRowId(row);
    const isSelected = selectedRows.includes(rowId);
    return (
        <TableRow data-state={isSelected ? 'selected' : undefined}>
            {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
            ))}
        </TableRow>
    );
});

/**
 * Renders a TanStack-powered table for the Purchase Orders V2 page.
 * Supports prev/next pagination.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} [props.data=[]] - Purchase order rows.
 * @param {number} [props.totalCount=0] - Total number of matching records from the server.
 * @param {boolean} [props.isLoading=false] - Initial loading state.
 * @param {boolean} [props.isFetching=false] - Whether any fetch is in progress.
 * @param {boolean} [props.hasNextPage=false] - Whether there is a next page (paginated mode).
 * @param {boolean} [props.hasPrevious=false] - Whether there is a previous page (paginated mode).
 * @param {() => void} [props.onPrevious] - Callback to go to previous page (paginated mode).
 * @param {() => void} [props.onNext] - Callback to go to next page (paginated mode).
 * @param {(purchaseOrder: Object) => void} [props.onCancelPurchaseOrder] - Callback to cancel a PO.
 * @param {(purchaseOrder: Object) => void} [props.onDuplicatePurchaseOrder] - Callback to duplicate a PO.
 * @param {(purchaseOrder: Object) => void} [props.onDownloadCsv] - Callback to download CSV.
 * @param {(purchaseOrder: Object) => void} [props.onDownloadPdf] - Callback to download PDF.
 * @param {(purchaseOrder: Object) => void} [props.onDownloadShipList] - Callback to download ship list.
 * @param {(purchaseOrder: Object) => void} [props.onSendToVendor] - Callback to send to vendor.
 * @param {(purchaseOrder: Object) => boolean} [props.isSendToVendorDisabled] - Guardrail for sending to vendor.
 * @param {({ text: string, level: 'success'|'error'|'warning'|'info' }) => void} [props.onNotify] - Callback for success/error/warning toasts (used by bulk actions).
 * @param {number} [props.currentPage] - Current page number (paginated mode).
 * @param {number} [props.totalPages] - Total number of pages (paginated mode).
 * @param {number} [props.pageSize] - Current page size for the page size selector.
 * @param {(size: number) => void} [props.onPageSizeChange] - Callback when page size changes.
 * @param {number[]} [props.pageSizeOptions] - Available page size options.
 * @param {Array<{id: string, desc: boolean}>} [props.sorting=[]] - TanStack table sorting state.
 * @param {(updater: Function|Array) => void} [props.onSortingChange] - Callback when sort changes.
 * @param {string} [props.emptyMessage='No purchase orders found.'] - Message when table is empty.
 * @returns {JSX.Element} Purchase orders data table.
 */
export function PurchaseOrdersTable({
    currentPage,
    data = [],
    emptyMessage = 'No purchase orders found.',
    hasNextPage = false,
    hasPrevious = false,
    isFetching = false,
    isLoading = false,
    isSendToVendorDisabled,
    onCancelPurchaseOrder,
    onDownloadCsv,
    onDownloadPdf,
    onDownloadShipList,
    onDuplicatePurchaseOrder,
    onNext,
    onNotify,
    onPageSizeChange,
    onPrevious,
    onSendToVendor,
    onSortingChange,
    pageSize,
    pageSizeOptions,
    sorting,
    totalCount = 0,
    totalPages,
}) {
    const [selectedRows, setSelectedRows] = useState([]);
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const history = useHistory();
    const { bulkDownloadCsvMutation, bulkDownloadPdfsMutation, bulkSendToVendorMutation } =
        useBulkPurchaseOrderTableMutations({ onNotify });
    const bulkActionLoading =
        bulkDownloadCsvMutation.isPending || bulkDownloadPdfsMutation.isPending || bulkSendToVendorMutation.isPending;

    /**
     * Navigates to the purchase order entity view for the selected row,
     * preserving the current query string so filter state can be restored.
     */
    const handleViewPurchaseOrder = useCallback(
        (purchaseOrder) => {
            const purchaseOrderId = purchaseOrder?.id;
            const currentFilters =
                (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ?? '';

            history.push({
                pathname: purchaseOrderId
                    ? `/procurement/viewPurchaseOrder-v2/${purchaseOrderId}`
                    : '/procurement/viewPurchaseOrder-v2/unknown',
                state: {
                    purchaseOrder: purchaseOrder ?? null,
                    purchaseOrdersFilters: currentFilters,
                },
            });
        },
        [history],
    );

    const renderSelectCell = useCallback(
        ({ row }) => {
            const rowId = getRowId(row);
            return (
                <Checkbox
                    checked={selectedRows.includes(rowId)}
                    onCheckedChange={(checked) => {
                        setSelectedRows((prev) => (checked ? [...prev, rowId] : prev.filter((id) => id !== rowId)));
                    }}
                    aria-label={selectedRows.includes(rowId) ? 'Deselect row' : 'Select row'}
                />
            );
        },
        [selectedRows],
    );

    const visibleRowIds = useMemo(() => tableData.map((r) => r?.id).filter(Boolean), [tableData]);

    const handleBulkSendToVendor = useCallback(() => {
        if (selectedRows.length === 0 || !onNotify) return;
        const selectedPOs = selectedRows.map((id) => tableData.find((row) => row?.id === id)).filter(Boolean);
        const notFoundCount = selectedRows.length - selectedPOs.length;

        if (notFoundCount > 0 && selectedPOs.length > 0) {
            onNotify({
                level: 'warning',
                text: `${notFoundCount} selected PO(s) not found on current page and will be skipped. Please navigate to those pages to send them.`,
            });
        }

        if (selectedPOs.length === 0) {
            onNotify({
                level: 'error',
                text: 'None of the selected POs are on the current page. Please navigate to the page with your selections.',
            });
            return;
        }

        const authorizedPOs = selectedPOs.filter((po) => !isSendToVendorDisabled?.(po));
        const unauthorizedCount = selectedPOs.length - authorizedPOs.length;

        if (authorizedPOs.length === 0) {
            onNotify({
                level: 'error',
                text: 'None of the selected POs can be sent. Only department leads can send POs at or above $200,000.',
            });
            return;
        }

        if (unauthorizedCount > 0) {
            onNotify({
                level: 'warning',
                text: `Skipping ${unauthorizedCount} PO(s) that require department lead authorization.`,
            });
        }

        bulkSendToVendorMutation.mutate(
            { purchaseOrderIds: authorizedPOs.map((po) => po.id) },
            {
                onSuccess: (sendResult) => {
                    if (sendResult.succeededPurchaseOrderIds?.length) {
                        setSelectedRows([]);
                    }
                },
            },
        );
    }, [bulkSendToVendorMutation, isSendToVendorDisabled, onNotify, selectedRows, tableData]);

    const handleBulkDownloadCsv = useCallback(() => {
        if (selectedRows.length === 0 || !onNotify) return;
        bulkDownloadCsvMutation.mutate(
            { purchaseOrderIds: selectedRows },
            {
                onSuccess: () => {
                    setSelectedRows([]);
                },
            },
        );
    }, [bulkDownloadCsvMutation, onNotify, selectedRows]);

    const handleBulkDownloadPdfs = useCallback(() => {
        if (selectedRows.length === 0) {
            if (onNotify) {
                onNotify({ level: 'info', text: 'No POs selected for PDF download.' });
            }
            return;
        }
        if (!onNotify) return;
        const pdfItems = selectedRows.map((id) => ({
            id,
            poRow: tableData.find((row) => row?.id === id) ?? null,
        }));
        bulkDownloadPdfsMutation.mutate(
            { pdfItems },
            {
                onSuccess: (pdfResult) => {
                    const successCount = pdfResult.results.filter((r) => r.success).length;
                    if (successCount > 0) {
                        setSelectedRows([]);
                    }
                },
            },
        );
    }, [bulkDownloadPdfsMutation, onNotify, selectedRows, tableData]);

    const renderSelectHeader = useCallback(() => {
        const count = visibleRowIds.length;
        const selectedCount = visibleRowIds.filter((id) => selectedRows.includes(id)).length;
        const allSelected = count > 0 && selectedCount === count;
        const someSelected = selectedCount > 0 && selectedCount < count;
        let checked = false;
        if (allSelected) checked = true;
        else if (someSelected) checked = 'indeterminate';

        const ariaLabel = checked === true ? 'Deselect all on page' : 'Select all on page';

        return (
            <Checkbox
                checked={checked}
                onCheckedChange={(next) => {
                    if (next === true) {
                        setSelectedRows((prev) => [...new Set([...prev, ...visibleRowIds])]);
                    } else {
                        setSelectedRows((prev) => prev.filter((id) => !visibleRowIds.includes(id)));
                    }
                }}
                aria-label={ariaLabel}
            />
        );
    }, [visibleRowIds, selectedRows]);

    const columns = useMemo(() => {
        const isSortable = (key) => Boolean(PurchaseOrderSortConfig.sortableFields[key]);
        return [
            {
                id: 'select',
                header: renderSelectHeader,
                enableSorting: false,
                cell: renderSelectCell,
            },
            {
                header: 'Facility',
                accessorKey: 'facilityName',
                enableSorting: false,
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'PO Number',
                accessorKey: 'id',
                enableSorting: isSortable('id'),
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'Created On',
                accessorKey: 'createdAt',
                enableSorting: isSortable('createdAt'),
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue()),
            },
            {
                header: 'Status',
                accessorKey: 'status',
                enableSorting: false,
                cell: renderStatusCell,
            },
            {
                header: 'Vendor',
                accessorKey: 'vendorName',
                enableSorting: false,
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'Pick Up Date',
                accessorFn: (row) => row?.pickupDate,
                enableSorting: false,
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue()),
            },
            {
                header: 'Scheduled Delivery',
                accessorKey: 'scheduledDeliveryDate',
                enableSorting: false,
                cell: renderDeliveryCell,
            },
            {
                id: 'buyerNames',
                header: 'Buyer Name(s)',
                accessorFn: (row) => row?.buyerNames ?? row?.buyers,
                enableSorting: false,
                cell: ({ getValue }) => formatBuyerNames(getValue()),
            },
            {
                id: 'produceOrGrocery',
                header: 'Produce / Grocery',
                accessorFn: (row) => row?.produceOrGrocery ?? row?.department,
                enableSorting: false,
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: renderActionsCell,
            },
        ];
    }, [renderSelectCell, renderSelectHeader]);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        state: {
            sorting: sorting ?? [],
        },
        onSortingChange,
        meta: {
            onCancelPurchaseOrder,
            onDuplicatePurchaseOrder,
            onDownloadCsv,
            onDownloadPdf,
            onDownloadShipList,
            onSendToVendor,
            isSendToVendorDisabled,
            onViewPurchaseOrder: handleViewPurchaseOrder,
        },
    });

    const loadedCount = tableData.length;
    const isRefreshing = isFetching;
    const selectionCount = selectedRows.length;
    const hasSelection = selectionCount > 0;
    const bulkControlsDisabled = !hasSelection || bulkActionLoading;

    return (
        <div className="flex flex-1 flex-col gap-2">
            {!isLoading && totalCount > 0 && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
                    <div className="shrink-0">
                        <ResultsCounter loadedCount={loadedCount} totalCount={totalCount} isRefreshing={isRefreshing} />
                    </div>
                    <div
                        className={cn(
                            'flex min-w-0 flex-1 flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-md bg-muted/40 px-2 py-1 transition-opacity',
                            !hasSelection && 'opacity-50',
                        )}
                        role="toolbar"
                        aria-label="Bulk actions for purchase orders"
                    >
                        <span className="text-sm font-medium text-foreground">
                            {selectionCount} {selectionCount === 1 ? 'item' : 'items'} selected
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                disabled={bulkControlsDisabled}
                                onClick={handleBulkSendToVendor}
                            >
                                {bulkActionLoading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                ) : null}
                                Send Emails to Vendors
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                disabled={bulkControlsDisabled}
                                onClick={handleBulkDownloadCsv}
                            >
                                {bulkActionLoading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                ) : null}
                                Download pre-populated csv
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                disabled={bulkControlsDisabled}
                                onClick={handleBulkDownloadPdfs}
                            >
                                {bulkActionLoading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                ) : null}
                                Download PDFs
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={bulkControlsDisabled}
                                onClick={() => setSelectedRows([])}
                                aria-label="Clear selection"
                            >
                                <X className="mr-1 h-4 w-4" aria-hidden />
                                Clear selection
                            </Button>
                        </div>
                    </div>
                    <div className="shrink-0">
                        <PurchaseOrderLegendButton />
                    </div>
                </div>
            )}

            <div className="flex min-h-[320px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading purchase orders…</span>
                        </div>
                    </div>
                ) : (
                    <div className="pb-6">
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            const canSort = header.column.getCanSort();
                                            const sorted = header.column.getIsSorted();
                                            return (
                                                <TableHead
                                                    key={header.id}
                                                    className={`whitespace-normal break-words${canSort ? ' cursor-pointer select-none' : ''}`}
                                                    onClick={
                                                        canSort ? header.column.getToggleSortingHandler() : undefined
                                                    }
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <div className="flex items-center gap-1">
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext(),
                                                            )}
                                                            {canSort &&
                                                                (() => {
                                                                    if (sorted === 'asc')
                                                                        return <ArrowUp className="h-4 w-4 shrink-0" />;
                                                                    if (sorted === 'desc')
                                                                        return (
                                                                            <ArrowDown className="h-4 w-4 shrink-0" />
                                                                        );
                                                                    return (
                                                                        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                                    );
                                                                })()}
                                                        </div>
                                                    )}
                                                </TableHead>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table
                                        .getRowModel()
                                        .rows.map((row) => (
                                            <MemoizedPurchaseOrderRow
                                                key={row.id}
                                                row={row}
                                                selectedRows={selectedRows}
                                            />
                                        ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {/* Pagination section */}
                        {tableData.length > 0 && (
                            <PaginationControls
                                hasPrevious={hasPrevious}
                                hasNext={hasNextPage}
                                onPrevious={onPrevious}
                                onNext={onNext}
                                isLoading={isFetching}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                onPageSizeChange={onPageSizeChange}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
